import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { EXPLORER_TYPE } from '../renderTypes'
import { getTableViewsFilters } from '~/services/explore'
import { OrderValue } from '~/types/cube'
import { SourceType } from '~/types/sources'
import { useCustomizationStore } from '~/stores/customization'
import { CustomizationField } from '~/types/customization'
import { DashboardModuleType, type DashboardConfig } from '~/types/dashboards'

export const createSalaryDashboardConfig = (): DashboardConfig => {
  const customizationStore = useCustomizationStore()
  const employeeGroupDimension = customizationStore.getCustomizationByField(
    CustomizationField.EMPLOYEE_GROUP
  )

  return [
    {
      titleKey: 'dashboard.salary.salaryRaiseMetrics',
      content: [
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey: 'dashboard.salary.countSalaryRaise.title',
              firstSubtitleKey: 'dashboard.salary.countSalaryRaise.total.title',
              secondSubtitleKey:
                'dashboard.salary.countSalaryRaise.ratio.title',
              infoContent: {
                definition: 'dashboard.salary.countSalaryRaise.info.definition',
                source: SourceType.HRIS,
                insight: 'dashboard.salary.countSalaryRaise.info.insight'
              },
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_SALARY_RAISE,
                type: EXPLORER_TYPE.BAR
              }
            },
            firstQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_SALARY_RAISE]
            },
            secondQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_RATIO_SALARY_RAISE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.salary.lastSalaryRaises.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.DATE_DAY,
                DIMENSION_TITLES.EMPLOYEE_SALARY_BASE_RAISE_RATE
              ],
              columnsTranslationPrefix:
                'dashboard.salary.lastSalaryRaises.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_SALARY_RAISE,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_SALARY_RAISE],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.DATE_DAY,
                DIMENSION_TITLES.EMPLOYEE_SALARY_BASE_RAISE_RATE
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.EMPLOYEE_COUNT_SALARY_RAISE
              ),
              order: [[DIMENSION_TITLES.DATE_DAY, OrderValue.DESC]],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.salary.employeesWithStuckSalary.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_TIME_WITHOUT_SALARY_RAISE
              ],
              columnsTranslationPrefix:
                'dashboard.salary.employeesWithStuckSalary.columns',
              exploreLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_WITHOUT_SALARY_RAISE,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_WITHOUT_SALARY_RAISE
              ],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_TIME_WITHOUT_SALARY_RAISE
              ],
              filters: getTableViewsFilters(MEASURE_TITLES.EMPLOYEE_COUNT_END),
              order: [
                [
                  DIMENSION_TITLES.EMPLOYEE_TIME_WITHOUT_SALARY_RAISE,
                  OrderValue.DESC
                ]
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.salary.timeToSalaryRaise.title',
              exploreLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_TO_SALARY_BASE_RAISE,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_TO_SALARY_BASE_RAISE
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_AVERAGE_TIME_TO_SALARY_BASE_RAISE
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.salary.salaryRaiseRate.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.moreData',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.salary.medianFteSalary.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_MEDIAN_FTE_SALARY_TOTAL_EUR,
                type: EXPLORER_TYPE.KPI
              },
              withoutTrend: true
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_MEDIAN_FTE_SALARY_TOTAL_EUR]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey: 'dashboard.salary.salary.title',
              firstSubtitleKey: 'dashboard.salary.salary.base.title',
              firstFillTrendMissingData: true,
              secondSubtitleKey:
                'dashboard.salary.salary.variableAndBonus.title',
              secondFillTrendMissingData: true,
              secondHasSpecialColor: true,
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_SUM_SALARY_TOTAL_EUR,
                dimension: employeeGroupDimension,
                type: EXPLORER_TYPE.KPI
              }
            },
            firstQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_SUM_SALARY_BASE_EUR]
            },
            secondQuery: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_SUM_SALARY_VARIABLE_AND_BONUS_EUR
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey:
                'dashboard.salary.countEmployeeWithVariableSalary.title',
              firstSubtitleKey:
                'dashboard.salary.countEmployeeWithVariableSalary.total.title',
              secondSubtitleKey:
                'dashboard.salary.countEmployeeWithVariableSalary.ratio.title',
              infoContent: {
                definition:
                  'dashboard.salary.countEmployeeWithVariableSalary.info.definition',
                source: SourceType.HRIS,
                insight:
                  'dashboard.salary.countEmployeeWithVariableSalary.info.insight'
              },
              exploreLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_COUNT_EMPLOYEE_WITH_VARIABLE_SALARY,
                type: EXPLORER_TYPE.LIST
              }
            },
            firstQuery: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_COUNT_EMPLOYEE_WITH_VARIABLE_SALARY
              ]
            },
            secondQuery: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_RATIO_EMPLOYEE_WITH_VARIABLE_SALARY
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.salary.avgFteSalaryTotalEUR.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_FTE_SALARY_TOTAL_EUR,
                type: EXPLORER_TYPE.KPI
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_FTE_SALARY_TOTAL_EUR]
            }
          }
        }
      ]
    }
  ]
}
